// Captura rapida: convierte una frase dictada en una tarea.
//
//   "proyecto B mejorar la UX de la pantalla de pedidos"
//     -> tarea MEJORA en el proyecto B, titulo "mejorar la UX de la pantalla
//        de pedidos"
//
// REGLA DE ORO: nunca perder una nota. Si no se entiende el proyecto, la tarea
// igual se guarda sin proyecto y cae en la Bandeja de entrada. Y `crudo`
// devuelve SIEMPRE el dictado original sin tocar: si el parser se equivoca al
// recortar el titulo, el texto entero queda guardado igual en tarea.detalle.
//
// Calculo puro, sin DOM: lo usan el navegador (para los chips en vivo) y el
// Worker. Va con tests, como todo lo de src/lib (AGENTS.md seccion 5).

import type { TipoTarea } from "./tipos";
import { sumarDias, proximoDiaSemana, finDeMes, DIAS_SEMANA } from "./fecha";

export interface ProyectoConocido {
  id: number;
  codigo: string | null;
  /** Como lo nombra en voz alta, separado por comas: "pedipan, pedi pan, pp" */
  alias: string | null;
  nombre: string;
}

export interface ResultadoCaptura {
  proyectoId: number | null;
  /** Texto limpio, sin las palabras que se consumieron. */
  titulo: string;
  tipo: TipoTarea;
  prioridad: number;
  venceEl: string | null;
  /** El dictado original, sin tocar. */
  crudo: string;
  /** BAJA = match difuso, o directo sin proyecto. No bloquea guardar. */
  confianza: "ALTA" | "BAJA";
}

// --------------------------------------------------------------- normalizar

/** Minusculas, sin tildes, sin puntuacion, espacios colapsados. */
export function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize("NFD")
    // Marcas de acento: NFD separa "á" en "a" + tilde, y aca se va la tilde.
    // Asi "mañana" queda "manana" y matchea aunque el dictado no ponga la eñe.
    .replace(/[̀-ͯ]/g, "")
    .replace(/[.,;:!?¡¿"'()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Distancia de edicion, para el match difuso. Sin dependencias. */
export function distancia(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let fila = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const nueva = [i];
    for (let j = 1; j <= b.length; j++) {
      nueva[j] = Math.min(
        fila[j] + 1,
        nueva[j - 1] + 1,
        fila[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    fila = nueva;
  }
  return fila[b.length];
}

// ------------------------------------------------------------- diccionarios

// El tipo NO se saca del titulo: "mejorar la UX" tiene que quedar entero.
const TIPOS: [TipoTarea, string[]][] = [
  ["BUG", ["bug", "error", "falla", "roto", "no anda", "no funciona"]],
  ["PAGO", ["pagar", "pago", "factura", "renovar", "vence la suscripcion", "abonar"]],
  [
    "TRAMITE",
    ["registrar", "registrarme", "sacar", "dar de alta", "pedir acceso", "tramite", "gestionar"],
  ],
  ["MEJORA", ["mejorar", "ux", "rediseniar", "redisenar", "queda feo", "ordenar"]],
  [
    "INVESTIGACION",
    ["averiguar", "investigar", "ver si", "probar si", "mirar como", "evaluar"],
  ],
];

// Estas SI se sacan del titulo. En la escala 1-10 de la app.
const PRIORIDADES: [number, string[]][] = [
  [1, ["urgente", "hoy mismo", "para ayer", "ya"]],
  [2, ["importante", "prioridad"]],
  [6, ["cuando pueda", "si hay tiempo"]],
  [9, ["algun dia", "idea suelta"]],
];

const NUMEROS: Record<string, number> = {
  un: 1, una: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5,
  seis: 6, siete: 7, ocho: 8, nueve: 9, diez: 10, quince: 15,
};

// ------------------------------------------------------------------ tokens

interface Tokens {
  /** Palabras como las escribio el usuario. */
  original: string[];
  /** Las mismas, normalizadas, para comparar. */
  norm: string[];
  /** Indices ya usados por el parser: no van al titulo. */
  usados: Set<number>;
}

function tokenizar(crudo: string): Tokens {
  const original = crudo.trim().split(/\s+/).filter(Boolean);
  return {
    original,
    norm: original.map((p) => normalizar(p)),
    usados: new Set<number>(),
  };
}

/**
 * Busca una frase (una o varias palabras) entre los tokens sin usar. Si la
 * encuentra, marca esos indices como usados y devuelve true.
 */
function consumirFrase(t: Tokens, frase: string, desde = 0): boolean {
  const partes = frase.split(" ");
  for (let i = desde; i <= t.norm.length - partes.length; i++) {
    let calza = true;
    for (let j = 0; j < partes.length; j++) {
      if (t.usados.has(i + j) || t.norm[i + j] !== partes[j]) {
        calza = false;
        break;
      }
    }
    if (calza) {
      for (let j = 0; j < partes.length; j++) t.usados.add(i + j);
      return true;
    }
  }
  return false;
}

/** Igual que consumirFrase pero sin marcar nada: sólo mira si está. */
function contieneFrase(norm: string[], frase: string): boolean {
  const partes = frase.split(" ");
  for (let i = 0; i <= norm.length - partes.length; i++) {
    if (partes.every((p, j) => norm[i + j] === p)) return true;
  }
  return false;
}

// ----------------------------------------------------------------- proyecto

/** Todas las formas de nombrar un proyecto: su codigo y sus alias. */
function formasDe(p: ProyectoConocido): string[] {
  const formas = [p.codigo ?? "", ...(p.alias ?? "").split(",")]
    .map((f) => normalizar(f))
    .filter(Boolean);
  return [...new Set(formas)];
}

interface MatchProyecto {
  id: number;
  largo: number; // cuantas palabras consume
  difuso: boolean;
}

/**
 * Cuantas ediciones se le perdonan al dictado, segun el largo de la forma.
 *
 * Los codigos cortos NO van a difuso: con tolerancia 2 sobre "MM", un "mmm"
 * de relleno terminaba archivando la nota en Mister Memoria. El spec es claro
 * en que preferimos una nota sin clasificar antes que una en el proyecto
 * equivocado, asi que abajo de 5 caracteres se exige match exacto.
 */
function toleranciaDe(forma: string): number {
  if (forma.length <= 4) return 0;
  if (forma.length <= 7) return 1;
  return 2;
}

/**
 * Busca el proyecto AL PRINCIPIO del texto, con o sin el prefijo "proyecto".
 *
 * Compara tambien la version sin espacios porque el dictado de Android parte
 * palabras: "pedipan" llega como "pedi pan".
 */
function detectarProyecto(t: Tokens, proyectos: ProyectoConocido[]): MatchProyecto | "EMPATE" | null {
  const inicio = t.norm[0] === "proyecto" ? 1 : 0;
  if (inicio >= t.norm.length) return null;

  const exactos: MatchProyecto[] = [];

  for (const p of proyectos) {
    for (const forma of formasDe(p)) {
      const sinEspacios = forma.replace(/ /g, "");
      // Se prueba consumiendo 1, 2 o 3 palabras del dictado.
      for (let largo = 1; largo <= 3 && inicio + largo <= t.norm.length; largo++) {
        const tramo = t.norm.slice(inicio, inicio + largo);
        if (tramo.join(" ") === forma || tramo.join("") === sinEspacios) {
          exactos.push({ id: p.id, largo: largo + inicio, difuso: false });
        }
      }
    }
  }

  if (exactos.length) {
    const ids = new Set(exactos.map((m) => m.id));
    // Empate entre dos proyectos distintos: no se adivina, va a Bandeja.
    if (ids.size > 1) return "EMPATE";
    // Del mismo proyecto, gana el match mas largo ("pedi pan" sobre "pedi").
    return exactos.reduce((a, b) => (b.largo > a.largo ? b : a));
  }

  // Sin match exacto: se prueba difuso sobre la primera y las dos primeras.
  const difusos: MatchProyecto[] = [];
  for (const p of proyectos) {
    for (const forma of formasDe(p)) {
      const sinEspacios = forma.replace(/ /g, "");
      const tolerancia = toleranciaDe(sinEspacios);
      // Los codigos cortos no van a difuso nunca: ver toleranciaDe.
      if (tolerancia === 0) continue;

      for (let largo = 1; largo <= 2 && inicio + largo <= t.norm.length; largo++) {
        const tramo = t.norm.slice(inicio, inicio + largo).join("");
        if (Math.abs(tramo.length - sinEspacios.length) > tolerancia) continue;
        if (distancia(tramo, sinEspacios) <= tolerancia) {
          difusos.push({ id: p.id, largo: largo + inicio, difuso: true });
        }
      }
    }
  }

  if (!difusos.length) return null;
  const ids = new Set(difusos.map((m) => m.id));
  if (ids.size > 1) return "EMPATE";
  return difusos.reduce((a, b) => (b.largo > a.largo ? b : a));
}

// -------------------------------------------------------------------- fecha

function detectarFecha(t: Tokens, hoy: string): string | null {
  if (consumirFrase(t, "pasado maniana") || consumirFrase(t, "pasado manana")) {
    return sumarDias(hoy, 2);
  }
  if (consumirFrase(t, "manana") || consumirFrase(t, "maniana")) return sumarDias(hoy, 1);
  if (consumirFrase(t, "hoy")) return hoy;
  if (consumirFrase(t, "la semana que viene") || consumirFrase(t, "semana que viene")) {
    return sumarDias(hoy, 7);
  }
  if (consumirFrase(t, "fin de mes") || consumirFrase(t, "a fin de mes")) return finDeMes(hoy);

  // "en 3 dias" / "en tres dias"
  for (let i = 0; i < t.norm.length - 2; i++) {
    if (t.usados.has(i) || t.norm[i] !== "en") continue;
    if (t.norm[i + 2] !== "dias" && t.norm[i + 2] !== "dia") continue;
    const n = Number(t.norm[i + 1]) || NUMEROS[t.norm[i + 1]];
    if (n) {
      t.usados.add(i);
      t.usados.add(i + 1);
      t.usados.add(i + 2);
      return sumarDias(hoy, n);
    }
  }

  // "el lunes", "el viernes", o el dia suelto.
  for (let dia = 0; dia < DIAS_SEMANA.length; dia++) {
    if (consumirFrase(t, `el ${DIAS_SEMANA[dia]}`) || consumirFrase(t, DIAS_SEMANA[dia])) {
      return proximoDiaSemana(hoy, dia);
    }
  }

  return null;
}

// ------------------------------------------------------------------ parser

export function analizarCaptura(
  crudo: string,
  proyectos: ProyectoConocido[],
  hoy: string,
): ResultadoCaptura {
  const t = tokenizar(crudo);

  // 1. Proyecto (al principio). Sus palabras se consumen.
  const match = detectarProyecto(t, proyectos);
  let proyectoId: number | null = null;
  let confianza: "ALTA" | "BAJA" = "BAJA";

  if (match && match !== "EMPATE") {
    proyectoId = match.id;
    confianza = match.difuso ? "BAJA" : "ALTA";
    for (let i = 0; i < match.largo; i++) t.usados.add(i);
  }

  // 2. Prioridad. Tambien se consume.
  let prioridad = 3;
  let hoyMismo = false;
  for (const [valor, frases] of PRIORIDADES) {
    for (const frase of frases) {
      if (consumirFrase(t, frase)) {
        prioridad = valor;
        // "hoy mismo" es urgencia Y fecha: quien lo dice quiere las dos cosas.
        if (frase === "hoy mismo") hoyMismo = true;
        break;
      }
    }
    if (prioridad !== 3) break;
  }

  // 3. Fecha. Tambien se consume.
  const venceEl = hoyMismo ? hoy : detectarFecha(t, hoy);

  // 4. Tipo. NO se consume: las palabras quedan en el titulo.
  let tipo: TipoTarea = "MODIFICACION";
  for (const [candidato, frases] of TIPOS) {
    if (frases.some((f) => contieneFrase(t.norm, f))) {
      tipo = candidato;
      break;
    }
  }

  const titulo = t.original.filter((_, i) => !t.usados.has(i)).join(" ").trim();

  return { proyectoId, titulo, tipo, prioridad, venceEl, crudo, confianza };
}
